import React, { useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { useIsSubTasksEnabled } from '@atlassian/jira-admin-pages-issue-types-resources/src/controllers/is-sub-tasks-enabled';
import { useIssueTypes } from '@atlassian/jira-admin-pages-issue-types-resources/src/controllers/issue-types/main.tsx';
import { toSearchFilterQsTransformer } from '@atlassian/jira-admin-pages-issue-types-resources/src/services/issue-types/utils/transformers.tsx';
import FilterSearch from '@atlassian/jira-common-analytics-v2-wrapped-components/src/filter-search';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { FireScreenAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouter } from '@atlassian/react-resource-router';
import { HeaderActions } from './header-actions';
import { messages } from './messages';
import { IssueTypesTable } from './table';

const DOC_LINKS = {
	WHAT_ARE_ISSUE_TYPES:
		'https://support.atlassian.com/jira-cloud-administration/docs/what-are-issue-types',
};

export const IssueTypesView = () => {
	const { formatMessage } = useIntl();

	const [, { updateFilter, fetchIssueTypes }] = useIssueTypes();

	const [routerState, routerActions] = useRouter();

	const onSearch = useCallback(
		(value: string, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'filterSearch changed', 'issueTypeName');
			updateFilter(
				routerState.location.search,
				{ searchFilter: value, page: 1 },
				routerActions.replace,
			);
		},
		[routerActions.replace, routerState.location.search, updateFilter],
	);

	const [, { getIsIssueTypeSubTasksEnabled }] = useIsSubTasksEnabled();

	useEffect(() => {
		getIsIssueTypeSubTasksEnabled();
	}, [fetchIssueTypes, getIsIssueTypeSubTasksEnabled, routerState.location.search]);

	return (
		<>
			<DocumentTitle title={formatMessage(messages.pageHeader)} />
			<PageHeader
				actions={<HeaderActions />}
				bottomBar={
					<>
						<HeaderDescription>
							<FormattedMessage
								{...messages.pageInfo}
								values={{
									b: (chunks) => <b>{chunks}</b>,
								}}
							/>{' '}
							<a
								href={DOC_LINKS.WHAT_ARE_ISSUE_TYPES}
								title={formatMessage(messages.pageInfoLinkTitle)}
							>
								{formatMessage(messages.pageInfoLinkSpork)}
							</a>
						</HeaderDescription>
						{/* @ts-expect-error - Type '{ placeholder: string; value: string; onChange: (value: string, analyticsEvent: UIAnalyticsEvent) => void; increasedWidth: true; placeholderAlwaysVisible: true; isAlwaysExpanded: true; }' is missing the following properties from type 'Omit<{ value: string; placeholder: string; describedById?: string | undefined; hasAutoFocus: boolean; isCompact: boolean; onChange: (value: string, args_1: UIAnalyticsEvent) => void; ... 6 more ...; key?: Key | ... 1 more ... | undefined; }, keyof WithAnalyticsEventsProps>': hasAutoFocus, isCompact */}
						<FilterSearch
							placeholder={formatMessage(messages.filterPlaceholder)}
							value={toSearchFilterQsTransformer(routerState.location.search)}
							onChange={onSearch}
							increasedWidth
							placeholderAlwaysVisible
							isAlwaysExpanded
						/>
					</>
				}
			>
				{formatMessage(messages.pageHeader)}
			</PageHeader>
			<IssueTypesTable />
			<FireScreenAnalytics />
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderDescription = styled.div({
	padding: `${token('space.100', '8px')} 0 ${token('space.200', '16px')}`,
	color: `${token('color.text', 'inherit')}`,
});
